import { watch, unref } from "vue";
import { useStore } from "vuex";

function ensureMeta(selector, attrName, attrValue, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (attrName === "name") el.setAttribute("name", attrValue);
    else if (attrName === "property") el.setAttribute("property", attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function interpolate(template, vars) {
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "");
}

const META_TAGS = [
  { selector: 'meta[name="description"]', attr: "name", value: "description" },
  { selector: 'meta[name="keywords"]', attr: "name", value: "keywords" },
  {
    selector: 'meta[property="og:title"]',
    attr: "property",
    value: "og:title",
  },
  {
    selector: 'meta[property="og:description"]',
    attr: "property",
    value: "og:description",
  },
  {
    selector: 'meta[property="og:image"]',
    attr: "property",
    value: "og:image",
  },
  { selector: 'meta[property="og:url"]', attr: "property", value: "og:url" },
  { selector: 'meta[property="og:type"]', attr: "property", value: "og:type" },
  {
    selector: 'meta[property="og:site_name"]',
    attr: "property",
    value: "og:site_name",
  },
  {
    selector: 'meta[name="twitter:card"]',
    attr: "name",
    value: "twitter:card",
  },
  {
    selector: 'meta[name="twitter:title"]',
    attr: "name",
    value: "twitter:title",
  },
  {
    selector: 'meta[name="twitter:description"]',
    attr: "name",
    value: "twitter:description",
  },
  {
    selector: 'meta[name="twitter:image"]',
    attr: "name",
    value: "twitter:image",
  },
];

export function applySEOPage(pageName, overrides, storeState) {
  if (typeof window === "undefined") return;

  const info = storeState?.company?.info;
  if (!info?.seo) return;

  const seo = info.seo;
  const pageConfig = seo.pages?.[pageName] || {};
  const siteName = seo.siteName || info.shortName || "";

  // Determine page title — prefer titleTemplate with overrides, fallback to title
  let pageTitle = "";
  if (Object.keys(overrides).length > 0 && pageConfig.titleTemplate) {
    pageTitle = interpolate(pageConfig.titleTemplate, overrides);
  } else if (pageConfig.title) {
    pageTitle = pageConfig.title;
  }

  // Apply global title template
  const finalTitle = interpolate(seo.defaultTitleTemplate || "{pageTitle}", {
    pageTitle,
    siteName,
  });
  document.title = finalTitle;

  // Determine description
  let desc = "";
  if (pageConfig.description) {
    desc = interpolate(pageConfig.description, overrides);
  }
  if (!desc) desc = seo.defaultDescription || "";

  // Determine keywords
  let kw = "";
  if (pageConfig.keywords) {
    kw = interpolate(pageConfig.keywords, overrides);
  }
  if (!kw) kw = seo.defaultKeywords || "";

  // Apply all meta tags
  ensureMeta(
    META_TAGS[0].selector,
    META_TAGS[0].attr,
    META_TAGS[0].value,
    desc,
  );
  ensureMeta(META_TAGS[1].selector, META_TAGS[1].attr, META_TAGS[1].value, kw);
  ensureMeta(
    META_TAGS[2].selector,
    META_TAGS[2].attr,
    META_TAGS[2].value,
    finalTitle,
  );
  ensureMeta(
    META_TAGS[3].selector,
    META_TAGS[3].attr,
    META_TAGS[3].value,
    desc,
  );
  ensureMeta(
    META_TAGS[5].selector,
    META_TAGS[5].attr,
    META_TAGS[5].value,
    window.location.href,
  );
  ensureMeta(
    META_TAGS[6].selector,
    META_TAGS[6].attr,
    META_TAGS[6].value,
    "website",
  );
  ensureMeta(
    META_TAGS[7].selector,
    META_TAGS[7].attr,
    META_TAGS[7].value,
    siteName,
  );

  const ogImage = overrides.ogImage || seo.defaultOgImage;
  if (ogImage) {
    ensureMeta(
      META_TAGS[4].selector,
      META_TAGS[4].attr,
      META_TAGS[4].value,
      ogImage,
    );
  }

  const twitterCard = seo.defaultTwitterCard || "summary_large_image";
  ensureMeta(
    META_TAGS[8].selector,
    META_TAGS[8].attr,
    META_TAGS[8].value,
    twitterCard,
  );
  ensureMeta(
    META_TAGS[9].selector,
    META_TAGS[9].attr,
    META_TAGS[9].value,
    finalTitle,
  );
  ensureMeta(
    META_TAGS[10].selector,
    META_TAGS[10].attr,
    META_TAGS[10].value,
    desc,
  );
  if (ogImage) {
    ensureMeta(
      META_TAGS[11].selector,
      META_TAGS[11].attr,
      META_TAGS[11].value,
      ogImage,
    );
  }
}

export function useSEO(pageName, overrides) {
  if (typeof window === "undefined") return;

  const store = useStore();

  const apply = () => {
    const o = unref(overrides);
    applySEOPage(pageName, o || {}, store.state);
  };

  apply();

  if (overrides && typeof overrides === "object" && "value" in overrides) {
    watch(overrides, apply, { deep: true });
  }
  watch(() => store.state.company.info?.seo, apply, { deep: true });
}
